// Modo simulação — Fase 11.
//
// Roda o fluxo numa conversa que não existe e mostra o caminho percorrido, com
// o que SERIA enviado. O canvas acende os passos por onde o run passou.
//
// A palavra que precisa estar visível o tempo todo é "nada foi enviado". Uma
// tela de simulação que parece a tela de execução faz alguém acreditar que
// mandou mensagem para o cliente — ou, pior, acreditar que não mandou.

import React, { useCallback, useState } from 'react';
import { AlertTriangle, Loader2, MessageSquare, Play, Plus, ShieldCheck, X } from 'lucide-react';
import type { Grafo } from '../tipos';
import type { ResultadoSimulacao } from '../apiGatilhos';
import { simular } from '../apiGatilhos';

interface Props {
  grafo: Grafo;
  versaoId: string | null;
  flowId: string;
  /** Fluxo com erro nem chega a simular: o motor pararia no primeiro passo. */
  podeSimular: boolean;
  onCaminho: (caminho: string[]) => void;
  onFechar: () => void;
}

const ROTULO_STATUS: Record<string, { texto: string; classe: string }> = {
  concluido: { texto: 'Concluiu', classe: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  aguardando: { texto: 'Parou esperando', classe: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  falhou: { texto: 'Falhou', classe: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  interrompido: { texto: 'Interrompido', classe: 'bg-secondary/20 text-[#8A5D0F] dark:text-secondary' },
  ativo: { texto: 'Não terminou', classe: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300' },
};

export default function PainelSimulacao({ grafo, versaoId, podeSimular, onCaminho, onFechar }: Props) {
  const [respostas, setRespostas] = useState<string[]>(['']);
  const [resultado, setResultado] = useState<ResultadoSimulacao | null>(null);
  const [rodando, setRodando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const quantosAguardam = grafo.nos.filter((n) => n.tipo === 'aguardar_resposta').length;

  const rodar = useCallback(async () => {
    if (!versaoId) {
      setErro('Salve o rascunho antes de simular — a simulação lê o fluxo do banco.');
      return;
    }
    setRodando(true);
    setErro(null);
    try {
      const r = await simular(versaoId, respostas.filter((x) => x.trim() !== ''));
      setResultado(r);
      onCaminho(r.caminho);
    } catch (e) {
      setErro((e as Error).message);
      setResultado(null);
      onCaminho([]);
    } finally {
      setRodando(false);
    }
  }, [onCaminho, respostas, versaoId]);

  return (
    <aside className="flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-700">
      <header className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-slate-700">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-bold text-gray-800 dark:text-white">
            <Play size={14} className="text-primary" />
            Simulação
          </p>
          <p className="mt-0.5 flex items-center gap-1 text-[11px] font-medium leading-snug text-emerald-600 dark:text-emerald-400">
            <ShieldCheck size={11} />
            Nada é enviado para ninguém.
          </p>
        </div>
        <button
          onClick={onFechar}
          className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700"
        >
          <X size={15} />
        </button>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
        {/* O roteiro de respostas. Um fluxo sem "aguardar resposta" não precisa
            de nenhuma — mostrar o campo ali seria pedir algo que não é usado. */}
        {quantosAguardam > 0 && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
              O que o cliente responderia
            </p>
            <p className="mb-1.5 mt-0.5 text-[11px] leading-snug text-gray-400 dark:text-slate-500">
              Uma resposta por passo de espera. Este fluxo tem {quantosAguardam}.
            </p>
            <div className="space-y-1.5">
              {respostas.map((r, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="w-4 shrink-0 text-center text-[11px] font-semibold text-gray-400">{i + 1}</span>
                  <input
                    value={r}
                    onChange={(e) =>
                      setRespostas((atual) => atual.map((x, j) => (j === i ? e.target.value : x)))
                    }
                    placeholder="sim"
                    className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                  />
                  {respostas.length > 1 && (
                    <button
                      onClick={() => setRespostas((atual) => atual.filter((_, j) => j !== i))}
                      className="rounded-md p-1 text-gray-300 transition-colors hover:bg-gray-100 hover:text-red-500 dark:hover:bg-slate-700"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {respostas.length < 10 && (
              <button
                onClick={() => setRespostas((atual) => [...atual, ''])}
                className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-primary transition-opacity hover:opacity-70"
              >
                <Plus size={11} />
                mais uma resposta
              </button>
            )}
          </div>
        )}

        <button
          onClick={() => void rodar()}
          disabled={rodando || !podeSimular}
          title={!podeSimular ? 'Corrija os erros do fluxo antes de simular' : undefined}
          className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {rodando ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
          Rodar simulação
        </button>

        {!podeSimular && (
          <p className="rounded-lg bg-gray-100 px-2.5 py-1.5 text-[11px] leading-snug text-gray-600 dark:bg-slate-700/50 dark:text-slate-300">
            O fluxo tem erro. Simular assim mostraria o motor parando no primeiro passo quebrado —
            o que a lista de problemas já diz, com mais precisão.
          </p>
        )}

        {erro && (
          <p className="flex items-start gap-1.5 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] leading-snug text-red-700 dark:bg-red-900/20 dark:text-red-300">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {erro}
          </p>
        )}

        {resultado && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                ROTULO_STATUS[resultado.status]?.classe ?? ROTULO_STATUS.ativo.classe
              }`}>
                {ROTULO_STATUS[resultado.status]?.texto ?? resultado.status}
              </span>
              <span className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-slate-700 dark:text-slate-300">
                <MessageSquare size={10} />
                {resultado.mensagens} {resultado.mensagens === 1 ? 'mensagem' : 'mensagens'}
              </span>
            </div>

            {resultado.motivo && (
              <p className="rounded-lg bg-gray-100 px-2.5 py-1.5 text-[11px] leading-snug text-gray-600 dark:bg-slate-700/50 dark:text-slate-300">
                {resultado.motivo}
              </p>
            )}

            <ol className="space-y-1.5">
              {resultado.passos.map((p, i) => (
                <li key={i} className="rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-slate-700/40">
                  <p className="text-[11px] font-semibold text-gray-700 dark:text-slate-200">
                    {i + 1}. {p.noKey ?? 'fim'}
                  </p>
                  <p className="text-[11px] leading-snug text-gray-500 dark:text-slate-400">{p.registro}</p>
                  {p.efeitos.map((e, j) => (
                    <p
                      key={j}
                      className="mt-1 rounded-md bg-white px-2 py-1 text-[11px] leading-snug text-gray-700 ring-1 ring-gray-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600"
                    >
                      {e.descricao}
                    </p>
                  ))}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </aside>
  );
}
